/**
 * Centralized, typed query keys — precise invalidation depends on them
 * (CLAUDE.md §7.4). Never inline a key array at a call site.
 */
export const slotKeys = {
  all: ['slots'] as const,
  day: (practitionerId: string, isoDate: string, service = 'in_person') =>
    ['slots', practitionerId, isoDate, service] as const,
  month: (practitionerId: string, isoMonth: string, service = 'in_person') =>
    ['slots', practitionerId, 'month', isoMonth, service] as const,
} as const;

export const appointmentKeys = {
  all: ['appointments'] as const,
  mine: (kind = 'in_person') =>
    kind === 'in_person'
      ? (['appointments', 'mine'] as const)
      : (['appointments', 'mine', kind] as const),
  detail: (appointmentId: string) => ['appointments', appointmentId] as const,
} as const;

/**
 * رسید. Keyed two ways because it is reached two ways — by the scanned code,
 * and by the appointment id the patient's own browser holds. Same document,
 * different doors, so a stale entry under one key must not be served under the
 * other.
 */
/**
 * پیگیری ویزیت. Keyed by visit rather than by appointment, because the visit is
 * what exists once «مراجعه شد» was recorded and is what the thread hangs off.
 *
 * `inbox` is the clinic's queue and is deliberately a sibling rather than a
 * child: answering one thread changes both, and the two are refetched together
 * by invalidating `all`.
 */
export const visitKeys = {
  all: ['visits'] as const,
  /** `kind` keeps a visit's thread and a review's apart even if their ids collided. */
  thread: (visitId: string, kind = 'visit') => ['visits', kind, visitId, 'thread'] as const,
  inbox: (kind = '') => ['visits', 'inbox', kind] as const,
} as const;

/** «بررسی آزمایش» — the patient's own, and one patient's as the clinic sees them. */
export const labReviewKeys = {
  all: ['lab-reviews'] as const,
  mine: () => ['lab-reviews', 'mine'] as const,
  ofPatient: (patientId: string) => ['lab-reviews', 'patient', patientId] as const,
  /** «سوابق» under the panel's queue — every patient's, answered or not. */
  history: () => ['lab-reviews', 'history'] as const,
} as const;

/** «مشاوره‌های امروز» — the doctor's phone consultations, one clinic day at a time. */
export const consultationKeys = {
  all: ['consultations'] as const,
  day: (isoDate: string) => ['consultations', 'day', isoDate] as const,
} as const;

export const receiptKeys = {
  all: ['receipts'] as const,
  byCode: (code: string) => ['receipts', 'code', code] as const,
  ofAppointment: (appointmentId: string) => ['receipts', 'appointment', appointmentId] as const,
} as const;

/**
 * The panel's داشبورد. Range-keyed on both sides so moving the window reads its
 * own entry rather than refetching over the previous one — and moving back is
 * instant. The report and the table are separate keys because they are separate
 * endpoints: a filter on the table must not discard the summary above it.
 */
export const bookingReportKeys = {
  all: ['booking-report'] as const,
  range: (from: string, to: string) => ['booking-report', from, to] as const,
} as const;

/**
 * «صف بیماران» — today's arrivals.
 *
 * Keyed by the clinic day rather than by nothing, so that a screen left open
 * across midnight refetches under a new key instead of appending to yesterday's
 * queue. There is no filter dimension: the queue has no pages and no search,
 * because a list of eight people needs neither.
 */
export const arrivalKeys = {
  all: ['arrivals'] as const,
  day: (date: string) => ['arrivals', date] as const,
  history: (days: number) => ['arrivals', 'history', days] as const,
} as const;

/**
 * آمار مطب and درآمد, range-keyed like the report they sit under. Separate
 * keys because they are separate endpoints behind separate permissions: a
 * secretary's screen refetching the appointment figures must not drag the money
 * along, and a role without `payments.view` never has an entry at all.
 */
export const clinicInsightsKeys = {
  all: ['clinic-insights'] as const,
  range: (from: string, to: string) => ['clinic-insights', from, to] as const,
} as const;

export const paymentSummaryKeys = {
  all: ['payment-summary'] as const,
  range: (from: string, to: string) => ['payment-summary', from, to] as const,
} as const;

export const staffAppointmentKeys = {
  all: ['staff-appointments'] as const,
  /**
   * The window, its status filter, the term typed into its search box and the
   * order it is read in. The term belongs in the key for the same reason the
   * filter does: it is a different question, not a refinement of the answer
   * already cached, and sharing a key across terms shows the previous term's
   * rows for a beat. The order is in it so that switching back to one already
   * fetched is instant instead of a refetch over the top of the other.
   *
   * `from`/`to` are the literal `all` when no date filter is in force — a key
   * segment has to be a value, and «همه نوبت‌ها» is a window like any other.
   *
   * The page and its size are in the key too: each page is its own answer, so
   * stepping back to one already read is instant and stepping forward never
   * shows the previous page's rows under the new page's number.
   */
  range: (
    from: string,
    to: string,
    status: string,
    search: string,
    order: string,
    page: number,
    perPage: number,
    kind = 'all',
  ) => ['staff-appointments', from, to, status, search, order, page, perPage, kind] as const,
  /**
   * One patient's whole history, on the account screen. A sibling of `range`
   * rather than a child: it is not a window over the same data, and a booking
   * made at the desk has to refresh both — which invalidating `all` does.
   */
  ofPatient: (patientId: string) => ['staff-appointments', 'patient', patientId] as const,
  /**
   * «سطل بازیافت». A sibling of `range` for the same reason `ofPatient` is: it
   * is not a window over the same rows but the complement of them, and deleting
   * or restoring has to refresh both — which invalidating `all` does.
   */
  deleted: () => ['staff-appointments', 'deleted'] as const,
} as const;

/**
 * پرداخت‌ها. Two keys because it is two questions: the panel's log over a range
 * of days, and the patient's own history — the same rows, different doors, so a
 * stale entry under one must never be served under the other.
 *
 * The range key carries its filters. A filter is a different question, not a
 * refinement of the answer already cached, and reusing one key across filters
 * shows last filter's rows for a beat every time it changes.
 */
export const paymentKeys = {
  all: ['payments'] as const,
  mine: () => ['payments', 'mine'] as const,
  // no range is the fixed 90-day window the tab's count is built from
  issues: (from = '', to = '') => ['payments', 'staff', 'issues', from, to] as const,
  range: (
    from: string,
    to: string,
    fromTime: string,
    toTime: string,
    status: string,
    gateway: string,
    search: string,
    purpose = '',
  ) => ['payments', 'staff', from, to, fromTime, toTime, status, gateway, search, purpose] as const,
} as const;

export const waitlistKeys = {
  all: ['waitlist'] as const,
  mine: () => ['waitlist', 'mine'] as const,
} as const;

export const patientKeys = {
  all: ['patients'] as const,
  ofAccount: () => ['patients', 'mine'] as const,
  /**
   * «این شماره را می‌شناسیم؟» — the panel's lookup before booking for someone.
   * Keyed by the number so retyping a digit does not serve the previous
   * patient's name while the new query is in flight.
   */
  byPhoneNumber: (phoneNumber: string) => ['patients', 'lookup', phoneNumber] as const,
} as const;

/**
 * Patient identity. Separate from `staffKeys` because the guards are separate
 * (CLAUDE.md §3.3) — one key would let a staff sign-out clear a patient's
 * session from the cache, and vice versa.
 */
export const accountKeys = {
  all: ['account'] as const,
  me: () => ['account', 'me'] as const,
} as const;

/** Staff identity. The token lives in a store; who you are is server state. */
export const staffKeys = {
  all: ['staff'] as const,
  me: () => ['staff', 'me'] as const,
} as const;

/**
 * Access control. Roles, the permission catalogue and the staff list all change
 * together — a role deleted changes who holds what — so the mutations
 * invalidate `all` and let the three refetch as one.
 */
export const roleKeys = {
  all: ['roles'] as const,
  list: () => ['roles', 'list'] as const,
  permissions: () => ['roles', 'permissions'] as const,
} as const;

export const staffUserKeys = {
  all: ['staff-users'] as const,
  list: () => ['staff-users', 'list'] as const,
} as const;

/**
 * Patient accounts as the panel sees them — the کاربران screen. Separate from
 * `accountKeys`, which is the signed-in patient's own identity: one is a staff
 * read of many accounts, the other is a patient's read of themselves, and a
 * blocked account must not evict a patient's session from the cache.
 *
 * The search term is part of the key, so each query caches its own result.
 */
export const patientAccountKeys = {
  all: ['patient-accounts'] as const,
  list: (search: string) => ['patient-accounts', 'list', search] as const,
  /**
   * One account opened. Its own key rather than a slice of a list entry: the
   * detail carries the *unmasked* number and the people the account books for,
   * which no list row holds — reading it out of a cached list would serve a
   * half-populated account.
   */
  detail: (accountId: string) => ['patient-accounts', 'detail', accountId] as const,
} as const;

/**
 * «شماره‌های مسدود». A sibling of patientAccountKeys rather than a child,
 * because blocking a number touches both: the entry appears here, and the
 * account behind it — if there is one — changes state over there. Both branches
 * are invalidated together after a write.
 */
export const blockedNumberKeys = {
  all: ['blocked-numbers'] as const,
  list: (search: string) => ['blocked-numbers', 'list', search] as const,
} as const;

export const practitionerKeys = {
  all: ['practitioners'] as const,
  list: () => ['practitioners', 'list'] as const,
} as const;

/**
 * The weekly schedule and the policy arrive in one payload and are written by
 * two endpoints, so both invalidate the same key.
 */
export const scheduleKeys = {
  all: ['schedule'] as const,
  of: (practitionerId: string, service = 'in_person') =>
    ['schedule', practitionerId, service] as const,
  slotPreview: (practitionerId: string, isoDate: string, service = 'in_person') =>
    ['schedule', practitionerId, service, 'slot-preview', isoDate] as const,
} as const;

export const dayOverrideKeys = {
  all: ['day-overrides'] as const,
  range: (practitionerId: string, fromIsoDate: string, toIsoDate: string, service = 'in_person') =>
    ['day-overrides', practitionerId, service, fromIsoDate, toIsoDate] as const,
} as const;

/**
 * The visit fee. One key, because there is one fee in force at a time — a new
 * version simply replaces what this returns (CLAUDE.md §3.5).
 */
export const feeKeys = {
  all: ['fee'] as const,
  /** One price list per service; no argument is the in-person visit. */
  current: (service = 'appointment') => ['fee', 'current', service] as const,
} as const;

/**
 * The tax rate. Staff-only and, like the fee, one version in force at a time.
 * Kept apart from `feeKeys` so saving a rate can refresh the quoted total
 * without discarding a fee the panel just wrote.
 */
export const taxKeys = {
  all: ['tax'] as const,
  current: () => ['tax', 'current'] as const,
} as const;

export const walletKeys = {
  all: ['wallet'] as const,
  /** «اعتبار من» — balance and recent ledger lines in one payload. */
  mine: () => ['wallet', 'mine'] as const,
  /**
   * One patient's wallet, read from the panel. A sibling of `mine` rather than
   * a child: they are two different endpoints behind two different guards, and
   * an admin adjusting a balance must not evict a patient's own session data.
   */
  ofAccount: (accountId: string) => ['wallet', 'account', accountId] as const,
  /** «گردش اعتبار» for the whole clinic, keyed by every filter it was asked with. */
  ledger: (filters: {
    readonly from: string;
    readonly to: string;
    readonly direction: string;
    readonly reason: string;
    readonly actor: string;
  }) => ['wallet', 'ledger', filters] as const,
} as const;

/**
 * شرایط لغو. One key, because one version is in force at a time — saving
 * appends a version and simply changes what this returns (CLAUDE.md §3.4).
 */
export const cancellationTermsKeys = {
  all: ['cancellation-terms'] as const,
  current: (kind = 'in_person') => ['cancellation-terms', 'current', kind] as const,
} as const;

/** یادآوری نوبت. Same shape and same reason as the cancellation terms. */
export const reminderScheduleKeys = {
  all: ['reminder-schedule'] as const,
  current: () => ['reminder-schedule', 'current'] as const,
} as const;

/**
 * «گفتگوی کارکنان». The list and every open conversation share `all`, so a
 * send or a delete can refresh both with one invalidation.
 */
export const staffChatKeys = {
  all: ['staff-chat'] as const,
  conversations: () => ['staff-chat', 'conversations'] as const,
  conversation: (colleagueId: string) => ['staff-chat', 'conversation', colleagueId] as const,
} as const;

/**
 * «پیام‌ها». The panel's sent list and the patient's inbox are siblings under
 * one root rather than one key: they sit behind different guards, and sending
 * a message must refresh the first without evicting a patient's own cache.
 * `audience` is keyed on every field that changes the answer.
 */
export const clinicMessageKeys = {
  all: ['clinic-messages'] as const,
  sent: () => ['clinic-messages', 'sent'] as const,
  sentPage: (page: number, perPage: number) =>
    ['clinic-messages', 'sent', { page, perPage }] as const,
  mine: () => ['clinic-messages', 'mine'] as const,
  audience: (audience: string, target: string) =>
    ['clinic-messages', 'audience', audience, target] as const,
} as const;

/**
 * The public site's content. Two keys because it is read through two doors: the
 * booking app reads the published view anonymously, the panel reads the
 * editable document with its version and image list. Saving refreshes both,
 * which invalidating `all` does.
 */
export const siteLandingKeys = {
  all: ['site-landing'] as const,
  /** …in the app's language; invalidating `published()` reaches every one. */
  published: () => ['site-landing', 'published'] as const,
  publishedIn: (locale: string) => ['site-landing', 'published', locale] as const,
  editable: () => ['site-landing', 'editable'] as const,
  /** The draft rendered as the site would render it — HTML, staff only. */
  preview: () => ['site-landing', 'preview'] as const,
  /** …in one language; invalidating `preview()` reaches both. */
  previewIn: (language: 'fa' | 'en') => ['site-landing', 'preview', language] as const,
} as const;
